import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { ApiError } from '@/api/http/types'
import { IdentityAuthPath } from '@/features/auth/lib/constants'

export const PASSWORD_CHANGE_LOCKOUT_SECONDS = 900

export const PASSWORD_CHANGE_LOCKOUT_MESSAGE = [
  'Too many failed attempts. Your ',
  'Password',
  ' Change has been locked for 15 minutes. Please try again later or use ',
  "'Forgot Password'",
  ' to reset.',
].join('')

export class PasswordLockoutError extends Error {
  readonly retryAfterSeconds: number

  constructor(retryAfterSeconds: number, message?: string) {
    super(message ?? PASSWORD_CHANGE_LOCKOUT_MESSAGE)
    this.name = 'PasswordLockoutError'
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function parseRetryAfterSeconds(retryRaw: unknown): number {
  return typeof retryRaw === 'number' && retryRaw > 0
    ? retryRaw
    : PASSWORD_CHANGE_LOCKOUT_SECONDS
}

export async function verifyCurrentPassword(
  currentPassword: string,
): Promise<{ verified: boolean }> {
  try {
    const { data: envelope } = await apiClient.post<
      ApiResponse<{ verified: boolean }>
    >(IdentityAuthPath.ME_PASSWORD_VERIFY_CURRENT, { currentPassword })
    if (!envelope.data) {
      throw new Error('Verify current password returned no data')
    }
    return envelope.data
  } catch (error) {
    if (error instanceof ApiError && error.status === 423) {
      throw new PasswordLockoutError(
        parseRetryAfterSeconds(error.data?.retry_after),
        error.message,
      )
    }
    if (axios.isAxiosError(error) && error.response?.status === 423) {
      const body = error.response.data as ApiResponse | undefined
      throw new PasswordLockoutError(
        parseRetryAfterSeconds(body?.meta?.retry_after),
        body?.message,
      )
    }
    throw error
  }
}

export async function changePassword(input: {
  newPassword: string
  confirmPassword: string
}): Promise<void> {
  await apiClient.patch(IdentityAuthPath.ME_PASSWORD, input)
}

export function useVerifyCurrentPassword() {
  return useMutation({ mutationFn: verifyCurrentPassword })
}

export function useChangePassword() {
  return useMutation({ mutationFn: changePassword })
}

export function getPasswordChangeErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof PasswordLockoutError) return error.message
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message) return error.message
  return fallback
}
